(() => {
  'use strict';
  if (customElements.get('urbrand-video-carousel')) return;

  class VideoCarousel extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.track = this.querySelector('[data-vc-track]');
      this.cards = [...this.querySelectorAll('[data-vc-card]:not([data-vc-clone])')];
      this.dialog = this.querySelector('[data-vc-dialog]');
      this.stage = this.querySelector('[data-vc-stage]');
      if (!this.track || !this.cards.length || !this.dialog || !this.stage) return;
      this.abort = new AbortController();
      const opts = { signal: this.abort.signal };
      this.mobile = matchMedia('(max-width:989px)');
      this.reduced = matchMedia('(prefers-reduced-motion:reduce)');
      this.active = -1;
      this.physicalIndex = -1;
      this.loopCopies = -1;
      this.touching = false;
      this.inView = false;
      this.userPaused = false;
      this.hovering = false;
      this.dialogIndex = -1;
      this.boundUpdate = () => this.updatePlayback();
      this.mobile.addEventListener('change', this.boundUpdate, opts);
      this.reduced.addEventListener('change', this.boundUpdate, opts);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stage.querySelector('video')?.pause();
        this.updatePlayback();
      }, opts);
      this.addEventListener('click', event => this.onClick(event), opts);
      this.addEventListener('pointerdown', event => this.onFocusInput(event), { ...opts, capture: true });
      // Listen outside the section too: Tab may enter a card from another widget.
      document.addEventListener('keydown', event => this.onFocusInput(event), { ...opts, capture: true });
      this.addEventListener('keydown', event => this.onKeydown(event), opts);
      this.addEventListener('mouseenter', () => { this.hovering = true; this.scheduleRotation(); }, opts);
      this.addEventListener('mouseleave', () => { this.hovering = false; this.scheduleRotation(); }, opts);
      this.addEventListener('focusin', () => this.scheduleRotation(), opts);
      this.addEventListener('focusout', () => queueMicrotask(() => { if (this.abort) this.scheduleRotation(); }), opts);
      this.track.addEventListener('scroll', () => {
        if (this.scrollTarget != null) { this.scheduleScrollSettle(); return; }
        this.scheduleScrollSettle();
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => { this.scrollFrame = 0; this.measureActive(); });
      }, { ...opts, passive: true });
      this.track.addEventListener('pointerdown', event => this.onPointerDown(event), opts);
      window.addEventListener('pointermove', event => this.onPointerMove(event), { ...opts, passive: false });
      window.addEventListener('pointerup', () => this.endDrag(), opts);
      window.addEventListener('pointercancel', () => this.endDrag(), opts);
      this.track.addEventListener('dragstart', event => event.preventDefault(), opts);
      this.dialog.addEventListener('close', () => { if (!this.dialog.open) this.cleanupDialog(); }, opts);
      this.dialog.addEventListener('click', event => {
        if (event.target === this.dialog) this.close();
      }, opts);
      document.addEventListener('urb:wishlist:updated', event => this.syncFavorite(event.detail?.items), opts);
      document.addEventListener('shopify:block:select', event => {
        if (!this.contains(event.target)) return;
        const card = event.target.closest('[data-vc-card]');
        if (card) this.go(this.cards.indexOf(card), { instant: true });
      }, opts);
      // Capture media events so dynamically-created visual copies share one handler.
      this.track.addEventListener('playing', event => {
        const video = event.target;
        if (!video.matches('[data-vc-preview]')) return;
        const card = video.closest('[data-vc-card]');
        if (card === this.currentCard() && this.canPreview()) card.classList.add('is-playing');
        else this.releasePreview(video);
      }, { ...opts, capture: true });
      for (const type of ['pause', 'error']) this.track.addEventListener(type, event => {
        if (event.target.matches('[data-vc-preview]')) event.target.closest('[data-vc-card]')?.classList.remove('is-playing');
      }, { ...opts, capture: true });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.inView = entries.some(entry => entry.isIntersecting);
          this.updatePlayback();
        }, { threshold: 0.15 });
        this.observer.observe(this.track);
      } else {
        // No viewport observer: keep poster-only until the visitor opens a video.
        this.inView = false;
      }
      if ('ResizeObserver' in window) {
        this.resize = new ResizeObserver(() => {
          cancelAnimationFrame(this.resizeFrame);
          this.resizeFrame = requestAnimationFrame(() => {
            this.buildLoop();
            if (this.active >= 0) this.go(this.active, { instant: true });
          });
        });
        this.resize.observe(this.track);
      }
      this.classList.add('is-ready');
      this.buildLoop();
      const initial = this.dataset.initial === 'middle' && !this.mobile.matches ? Math.floor((Math.min(this.cards.length, 5) - 1) / 2) : 0;
      this.go(initial, { instant: true });
    }

    enabled() {
      return this.dataset[this.mobile.matches ? 'mobile' : 'desktop'] !== 'false';
    }

    currentCard() {
      return this.renderedCards?.[this.physicalIndex] || this.cards[this.active];
    }

    buildLoop() {
      const count = this.cards.length;
      const cardWidth = Math.max(1, this.cards[0].getBoundingClientRect().width || 200);
      // Full cycles on each side fill wide screens even with only two videos.
      const copies = this.dataset.loop === 'true' && count > 1
        ? Math.max(1, Math.ceil((Math.ceil(this.track.clientWidth / cardWidth) + 2) / count)) : 0;
      if (copies === this.loopCopies) return;
      (this.renderedCards || this.cards).forEach(card => this.releasePreview(card.querySelector('[data-vc-preview]')));
      this.track.querySelectorAll('[data-vc-clone]').forEach(card => card.remove());
      this.loopCopies = copies;
      this.loopOffset = copies * count;
      const before = document.createDocumentFragment();
      const after = document.createDocumentFragment();
      for (let cycle = 0; cycle < copies; cycle++) {
        for (const card of this.cards) {
          before.append(this.cloneCard(card));
          after.append(this.cloneCard(card));
        }
      }
      this.track.prepend(before);
      this.track.append(after);
      this.renderedCards = [...this.track.querySelectorAll('[data-vc-card]')];
      this.physicalIndex = -1;
    }

    cloneCard(card) {
      const clone = card.cloneNode(true);
      clone.setAttribute('data-vc-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-shopify-editor-block');
      clone.removeAttribute('id');
      clone.classList.remove('is-active', 'is-playing');
      clone.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'));
      clone.querySelectorAll('[data-shopify-editor-block]').forEach(el => el.removeAttribute('data-shopify-editor-block'));
      clone.querySelectorAll('button,a,[tabindex]').forEach(el => el.setAttribute('tabindex', '-1'));
      clone.querySelectorAll('template,noscript').forEach(el => el.remove());
      const preview = clone.querySelector('[data-vc-preview]');
      preview?.removeAttribute('src');
      preview?.removeAttribute('data-vc-discoverable');
      preview?.removeAttribute('data-vc-started');
      return clone;
    }

    physicalTarget(index, instant) {
      const target = this.normalize(index);
      if (!this.loopOffset) return target;
      if (instant || this.physicalIndex < 0) return this.loopOffset + target;
      const delta = index - this.active;
      if (Math.abs(delta) === 1) return this.physicalIndex + delta;
      let closest = this.loopOffset + target;
      for (let candidate = target; candidate < this.renderedCards.length; candidate += this.cards.length) {
        if (Math.abs(candidate - this.physicalIndex) < Math.abs(closest - this.physicalIndex)) closest = candidate;
      }
      return closest;
    }

    rebaseLoop() {
      if (!this.loopOffset || this.drag || this.touching) return;
      const canonical = this.loopOffset + this.active;
      if (this.physicalIndex === canonical) return;
      const delta = this.renderedCards[canonical].getBoundingClientRect().left - this.currentCard().getBoundingClientRect().left;
      // Equivalent copies share identical geometry: shift an entire cycle invisibly.
      this.track.classList.add('is-rebasing');
      this.track.scrollTo({ left: this.track.scrollLeft + delta, behavior: 'instant' });
      this.setActive(this.active, canonical);
      void this.track.offsetWidth;
      this.track.classList.remove('is-rebasing');
    }

    canAnimate() {
      return this.enabled() && this.inView && !document.hidden && !this.reduced.matches && !navigator.connection?.saveData && !this.userPaused && !this.dialog.open;
    }

    canPreview() {
      return this.canAnimate() && this.dataset[this.mobile.matches ? 'previewMobile' : 'previewDesktop'] === 'true';
    }

    releasePreview(video) {
      if (!video) return;
      video.pause();
      video.closest('[data-vc-card]')?.classList.remove('is-playing');
      if (video.dataset.vcDiscoverable === 'true') {
        if (video.dataset.vcStarted === 'true') {
          delete video.dataset.vcStarted;
          video.preload = 'none';
          video.load();
        }
      } else if (video.hasAttribute('src')) { video.removeAttribute('src'); video.load(); }
    }

    updatePlayback() {
      if (!this.abort) return;
      if (!this.enabled() && this.dialog.open) this.close();
      const activeVideo = this.canPreview() ? this.currentCard()?.querySelector('[data-vc-preview]') : null;
      (this.renderedCards || this.cards).forEach(card => {
        const video = card.querySelector('[data-vc-preview]');
        if (video !== activeVideo) this.releasePreview(video);
      });
      if (activeVideo) {
        if (!activeVideo.hasAttribute('src')) activeVideo.src = activeVideo.dataset.src;
        activeVideo.muted = true;
        if (activeVideo.paused) {
          activeVideo.dataset.vcStarted = 'true';
          activeVideo.play()?.then(() => {
            if (!this.abort || !this.canPreview() || activeVideo !== this.currentCard()?.querySelector('[data-vc-preview]')) this.releasePreview(activeVideo);
          }).catch(() => {});
        }
      }
      const motion = this.querySelector('[data-vc-motion]');
      if (motion) {
        motion.hidden = this.dataset[this.mobile.matches ? 'previewMobile' : 'previewDesktop'] !== 'true' && this.dataset.rotate !== 'true';
        motion.setAttribute('aria-pressed', String(this.userPaused));
        motion.textContent = this.userPaused ? 'Retomar animações' : 'Pausar animações';
      }
      this.scheduleRotation();
    }

    normalize(index, count = this.cards.length) {
      return this.dataset.loop === 'true' ? ((index % count) + count) % count : Math.max(0, Math.min(count - 1, index));
    }

    go(index, { instant = false, focus = false } = {}) {
      if (!this.cards.length) return;
      const target = this.normalize(index);
      const immediate = instant || this.reduced.matches;
      let physical = this.physicalTarget(index, immediate);
      if (this.loopOffset && !this.renderedCards[physical]) {
        this.rebaseLoop();
        physical = this.physicalTarget(index, immediate);
      }
      const card = (this.renderedCards || this.cards)[physical];
      const rect = card.getBoundingClientRect();
      const trackRect = this.track.getBoundingClientRect();
      const left = this.track.scrollLeft + rect.left + rect.width / 2 - trackRect.left - this.track.clientWidth / 2;
      clearTimeout(this.settleTimer);
      this.scrollTarget = immediate ? null : physical;
      this.track.scrollTo({ left, behavior: immediate ? 'instant' : 'smooth' });
      this.setActive(target, physical);
      if (!immediate) this.scheduleScrollSettle();
      if (focus) this.cards[target].querySelector('[data-vc-open]')?.focus({ preventScroll: true });
    }

    scheduleScrollSettle() {
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => {
        this.scrollTarget = null;
        this.measureActive();
        this.rebaseLoop();
      }, 150);
    }

    measureActive() {
      const rect = this.track.getBoundingClientRect();
      const center = rect.left + this.track.clientWidth / 2;
      let closest = 0;
      let distance = Infinity;
      const rendered = this.renderedCards || this.cards;
      rendered.forEach((card, index) => {
        const bounds = card.getBoundingClientRect();
        const current = Math.abs(bounds.left + bounds.width / 2 - center);
        if (current < distance) { closest = index; distance = current; }
      });
      this.setActive(Number(rendered[closest].dataset.vcIndex), closest);
    }

    setActive(index, physical = index) {
      if (this.active === index && this.physicalIndex === physical) return;
      this.active = index;
      this.physicalIndex = physical;
      (this.renderedCards || this.cards).forEach((card, i) => card.classList.toggle('is-active', i === physical));
      const counter = this.querySelector('[data-vc-counter]');
      if (counter) counter.textContent = `${index + 1} / ${this.cards.length}`;
      this.setNavDisabled('[data-vc-prev]', '[data-vc-next]', index);
      this.updatePlayback();
    }

    setNavDisabled(prev, next, index) {
      const loops = this.dataset.loop === 'true';
      const previous = this.querySelector(prev);
      const following = this.querySelector(next);
      if (previous) previous.disabled = !loops && index === 0;
      if (following) following.disabled = !loops && index === this.cards.length - 1;
    }

    scheduleRotation() {
      clearTimeout(this.rotationTimer);
      if (!this.abort || !this.canAnimate() || this.dataset.rotate !== 'true' || this.dataset.designMode === 'true' || this.cards.length < 2 || this.hovering || this.drag || this.touching || this.contains(document.activeElement)) return;
      if (this.dataset.loop !== 'true' && this.active === this.cards.length - 1) return;
      this.rotationTimer = setTimeout(() => this.go(this.active + 1), Math.max(3000, Number(this.dataset.interval) || 6000));
    }

    open(index) {
      const card = this.cards[index];
      const template = card?.querySelector('[data-vc-template]');
      if (!template || !this.enabled()) return;
      const wasOpen = this.dialog.open;
      const restoreControlFocus = wasOpen && this.stage.contains?.(document.activeElement);
      if (!wasOpen) {
        document.querySelectorAll('urbrand-video-carousel').forEach(other => { if (other !== this) other.close?.(); });
        this.opener = card.querySelector('[data-vc-open]');
        this.dialogMuted = this.dataset.startMuted === 'true';
        this.dialogCaptions = true;
      }
      this.stage.querySelector('video')?.pause();
      this.mediaAbort?.abort();
      this.mediaAbort = new AbortController();
      this.stage.replaceChildren(template.content.cloneNode(true));
      this.dialogIndex = index;
      this.setupProgress();
      this.syncFavorite();
      this.querySelector('[data-vc-dialog-title]').textContent = card.dataset.vcTitle;
      this.querySelector('[data-vc-error]').hidden = true;
      const counter = this.querySelector('[data-vc-dialog-counter]');
      if (counter) counter.textContent = `${index + 1} / ${this.cards.length}`;
      this.setNavDisabled('[data-vc-dialog-prev]', '[data-vc-dialog-next]', index);
      if (!wasOpen) this.dialog.showModal();
      document.documentElement.classList.add('has-video-carousel-dialog');
      this.go(index, { instant: true });
      this.updatePlayback();
      const video = this.stage.querySelector('video');
      if (video) {
        const opts = { signal: this.mediaAbort.signal };
        video.controls = false;
        video.setAttribute('tabindex', '-1');
        // Configure inline playback before loading the video created from the template.
        // Keep load() and play() in the original tap's call stack for Safari.
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.preload = 'auto';
        video.defaultMuted = this.dialogMuted;
        video.muted = this.dialogMuted;
        const source = video.querySelector('source[src]');
        if (source) video.src = source.getAttribute('src');
        video.addEventListener('volumechange', () => { this.dialogMuted = video.muted; this.syncPlayerControls(); }, opts);
        for (const type of ['play', 'playing', 'pause', 'ended']) video.addEventListener(type, () => this.syncPlayerControls(), opts);
        for (const type of ['timeupdate', 'durationchange', 'loadedmetadata']) video.addEventListener(type, () => this.updateProgress(video), opts);
        video.addEventListener('playing', () => { this.querySelector('[data-vc-error]').hidden = true; }, opts);
        video.addEventListener('error', () => this.showPlayerError('Não foi possível reproduzir este vídeo. Tente novamente ou escolha outro.'), { ...opts, capture: true });
        const captions = video.querySelector('track[kind="captions"]');
        this.querySelector('[data-vc-captions]').hidden = !captions;
        captions?.addEventListener('load', () => this.applyCaptions(), opts);
        this.applyCaptions();
        video.addEventListener('ended', () => {
          if (this.dataset.autoNext === 'true' && this.cards.length > 1 && (this.dataset.loop === 'true' || index < this.cards.length - 1)) this.open(this.normalize(index + 1));
        }, opts);
        this.syncPlayerControls();
        video.load();
        this.playDialogVideo(video);
      }
      if (!wasOpen || restoreControlFocus) this.querySelector('[data-vc-close]').focus({ preventScroll: true });
    }

    setupProgress() {
      const progress = this.querySelector('[data-vc-progress]');
      if (!progress) return;
      if (progress.children.length !== this.cards.length) {
        progress.replaceChildren(...this.cards.map((card, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.vcJump = String(index);
          button.setAttribute('aria-label', `Ir para vídeo ${index + 1} de ${this.cards.length}`);
          button.title = `Vídeo ${index + 1} de ${this.cards.length}`;
          const fill = document.createElement('span');
          fill.setAttribute('aria-hidden', 'true');
          button.append(fill);
          return button;
        }));
      }
      this.updateProgress(this.stage.querySelector('video'));
    }

    updateProgress(video) {
      const fraction = Number.isFinite(video?.duration) && video.duration > 0
        ? Math.max(0, Math.min(1, video.currentTime / video.duration)) : 0;
      this.querySelectorAll('[data-vc-jump]').forEach((button, index) => {
        button.style.setProperty('--vc-progress', String(index < this.dialogIndex ? 1 : index === this.dialogIndex ? fraction : 0));
        if (index === this.dialogIndex) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
    }

    syncPlayerControls() {
      const video = this.stage.querySelector('video');
      if (!video) return;
      const paused = video.paused || video.ended;
      const pause = this.querySelector('[data-vc-pause]');
      const mute = this.querySelector('[data-vc-mute]');
      const pauseLabel = paused ? 'Reproduzir vídeo' : 'Pausar vídeo';
      pause.setAttribute('aria-label', pauseLabel);
      pause.title = pauseLabel;
      this.querySelector('[data-vc-icon-pause]').hidden = paused;
      this.querySelector('[data-vc-icon-play]').hidden = !paused;
      const muteLabel = video.muted ? 'Ativar som' : 'Desativar som';
      mute.setAttribute('aria-label', muteLabel);
      mute.setAttribute('aria-pressed', String(!video.muted));
      mute.title = muteLabel;
      this.querySelector('[data-vc-icon-muted]').hidden = !video.muted;
      this.querySelector('[data-vc-icon-volume]').hidden = video.muted;
    }

    playDialogVideo(video = this.stage.querySelector('video'), allowMutedRetry = true) {
      if (!video) return;
      video.play()?.then(() => {
        if (!this.abort || !this.dialog.open || document.hidden || video !== this.stage.querySelector('video')) video.pause();
      }).catch(error => {
        if (!this.abort || !this.dialog.open || document.hidden || video !== this.stage.querySelector('video') || error.name === 'AbortError') return;
        // If audible playback is denied, start silently without asking for a second tap.
        // A denied silent attempt still exposes the existing manual play action.
        if (error.name === 'NotAllowedError' && allowMutedRetry && !video.muted) {
          video.muted = true;
          this.dialogMuted = true;
          this.syncPlayerControls();
          this.playDialogVideo(video, false);
          return;
        }
        this.syncPlayerControls();
        this.showPlayerError(error.name === 'NotAllowedError' ? 'Toque em Reproduzir para assistir ao vídeo.' : 'Não foi possível reproduzir este vídeo. Tente novamente ou escolha outro.', error.name === 'NotAllowedError');
      });
    }

    showPlayerError(message, blocked = false) {
      this.querySelector('[data-vc-error-text]').textContent = message;
      this.querySelector('[data-vc-retry]').textContent = blocked ? 'Reproduzir' : 'Tentar novamente';
      this.querySelector('[data-vc-error]').hidden = false;
    }

    togglePlayer() {
      const video = this.stage.querySelector('video');
      if (!video) return;
      if (video.paused || video.ended) this.playDialogVideo(video);
      else video.pause();
    }

    applyCaptions() {
      const video = this.stage.querySelector('video');
      if (!video) return;
      const tracks = video.textTracks;
      // Indexed access also supports media track lists without an iterator.
      for (let i = 0; i < (tracks?.length || 0); i++) tracks[i].mode = this.dialogCaptions ? 'showing' : 'hidden';
      const button = this.querySelector('[data-vc-captions]');
      const label = this.dialogCaptions ? 'Desativar legendas' : 'Ativar legendas';
      button.setAttribute('aria-pressed', String(this.dialogCaptions));
      button.setAttribute('aria-label', label);
      button.title = label;
    }

    syncFavorite(items) {
      const favorite = this.stage.querySelector('[data-wishlist-toggle]');
      if (!favorite) return;
      // Reuse the theme's wishlist handler and storage; do not maintain a second list.
      if (!Array.isArray(items)) {
        try { items = JSON.parse(localStorage.getItem('urbrand:wishlist:v2') || localStorage.getItem('urbrand:wishlist:v1') || '[]'); } catch (_) { items = []; }
      }
      const active = Array.isArray(items) && items.some(item => String(item.id) === favorite.dataset.productId);
      favorite.classList.toggle('active', active);
      favorite.setAttribute('aria-pressed', String(active));
      const label = active ? 'Remover dos favoritos' : 'Adicionar aos favoritos';
      favorite.setAttribute('aria-label', label);
      favorite.title = label;
    }

    close() {
      if (this.dialog?.open) this.dialog.close();
    }

    cleanupDialog() {
      this.mediaAbort?.abort();
      this.stage?.querySelector('video')?.pause();
      this.stage?.replaceChildren();
      this.dialogIndex = -1;
      const anotherDialog = document.querySelector('urbrand-video-carousel dialog[open]');
      if (!anotherDialog) document.documentElement.classList.remove('has-video-carousel-dialog');
      if (!anotherDialog && this.isConnected && this.enabled()) this.opener?.focus({ preventScroll: true });
      this.updatePlayback();
    }

    onClick(event) {
      if (this.suppressClick) { event.preventDefault(); event.stopPropagation(); this.suppressClick = false; return; }
      const open = event.target.closest('[data-vc-open]');
      if (open) { this.open(Number(open.closest('[data-vc-card]').dataset.vcIndex)); return; }
      if (event.target.closest('[data-vc-close]')) { this.close(); return; }
      if (event.target.closest('[data-vc-pause]') || event.target.matches('.video-carousel__player')) { this.togglePlayer(); return; }
      if (event.target.closest('[data-vc-mute]')) {
        const video = this.stage.querySelector('video');
        if (video) { video.muted = !video.muted; this.dialogMuted = video.muted; this.syncPlayerControls(); }
        return;
      }
      if (event.target.closest('[data-vc-captions]')) { this.dialogCaptions = !this.dialogCaptions; this.applyCaptions(); return; }
      if (event.target.closest('[data-vc-retry]')) {
        const video = this.stage.querySelector('video');
        if (video?.error) video.load();
        this.playDialogVideo(video);
        return;
      }
      const jump = event.target.closest('[data-vc-jump]');
      if (jump) { this.open(Number(jump.dataset.vcJump)); return; }
      if (event.target.closest('[data-vc-prev]')) { this.go(this.active - 1); return; }
      if (event.target.closest('[data-vc-next]')) { this.go(this.active + 1); return; }
      if (event.target.closest('[data-vc-dialog-prev]')) { this.open(this.normalize(this.dialogIndex - 1)); return; }
      if (event.target.closest('[data-vc-dialog-next]')) { this.open(this.normalize(this.dialogIndex + 1)); return; }
      if (event.target.closest('[data-vc-motion]')) { this.userPaused = !this.userPaused; this.updatePlayback(); }
    }

    onFocusInput(event) {
      // Safari can retain :focus-visible when dialog.close() restores a touched card.
      // Keep real focus for accessibility; suppress only the pointer-origin card ring.
      if (event.type === 'pointerdown') this.dataset.vcPointerFocus = 'true';
      else if (event.type === 'keydown' && !event.metaKey && !event.altKey && !event.ctrlKey) delete this.dataset.vcPointerFocus;
    }

    onKeydown(event) {
      if (['VIDEO', 'INPUT', 'TEXTAREA', 'SELECT'].includes(event.target.tagName)) return;
      const key = event.key;
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(key)) return;
      if (!this.dialog.open && !event.target.closest('[data-vc-open]')) return;
      event.preventDefault();
      const focusedIndex = this.cards.indexOf(event.target.closest('[data-vc-card]'));
      const current = this.dialog.open ? this.dialogIndex : (focusedIndex >= 0 ? focusedIndex : this.active);
      const target = key === 'Home' ? 0 : key === 'End' ? this.cards.length - 1 : current + (key === 'ArrowLeft' ? -1 : 1);
      if (this.dialog.open) this.open(this.normalize(target)); else this.go(target, { focus: true });
    }

    onPointerDown(event) {
      this.suppressClick = false;
      this.scrollTarget = null;
      clearTimeout(this.settleTimer);
      if (event.pointerType !== 'mouse') { this.touching = true; this.scheduleRotation(); return; } // Native touch scrolling.
      if (event.button !== 0) return;
      event.preventDefault(); // Avoid native focus scrolling fighting the drag.
      this.drag = { x: event.clientX, left: this.track.scrollLeft, moved: false };
      clearTimeout(this.rotationTimer);
    }

    onPointerMove(event) {
      if (!this.drag) return;
      const delta = event.clientX - this.drag.x;
      if (Math.abs(delta) < 6 && !this.drag.moved) return;
      event.preventDefault();
      this.drag.moved = true;
      this.track.classList.add('is-dragging');
      this.track.scrollLeft = this.drag.left - delta;
    }

    endDrag() {
      if (this.touching) { this.touching = false; this.scheduleScrollSettle(); this.scheduleRotation(); }
      if (!this.drag) return;
      const moved = this.drag.moved;
      this.drag = null;
      if (moved) {
        this.suppressClick = true;
        this.measureActive();
        this.go(this.active, { instant: true });
        clearTimeout(this.clickTimer);
        this.clickTimer = setTimeout(() => { this.suppressClick = false; }, 0);
      }
      this.track.classList.remove('is-dragging');
      this.scheduleRotation();
    }

    disconnectedCallback() {
      if (!this.abort) return;
      clearTimeout(this.rotationTimer);
      clearTimeout(this.clickTimer);
      clearTimeout(this.settleTimer);
      cancelAnimationFrame(this.scrollFrame);
      cancelAnimationFrame(this.resizeFrame);
      this.scrollFrame = 0;
      this.resizeFrame = 0;
      this.scrollTarget = null;
      this.drag = null;
      this.touching = false;
      this.observer?.disconnect();
      this.resize?.disconnect();
      this.abort.abort();
      this.mediaAbort?.abort();
      this.abort = null;
      (this.renderedCards || this.cards).forEach(card => this.releasePreview(card.querySelector('[data-vc-preview]')));
      this.track.querySelectorAll('[data-vc-clone]').forEach(card => card.remove());
      this.renderedCards = this.cards;
      this.close();
      this.cleanupDialog();
      delete this.dataset.vcPointerFocus;
      this.classList.remove('is-ready');
    }
  }
  customElements.define('urbrand-video-carousel', VideoCarousel);
})();
